import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { MessageService } from "primeng/api";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { HttpService } from "../../services/http.service";
import { StateService } from "../../services/state.service";
import { AdminModalComponent } from "../../shared/admin-modal/admin-modal.component";
import { requestTypes } from "../admin.constants";
import { CheckboxChangeEvent } from "primeng/checkbox";
import { Table } from "primeng/table";
import { TranslateService } from "@ngx-translate/core";
@Component({
  selector: "app-admin-table",
  templateUrl: "./admin-table.component.html",
  styleUrls: ["./admin-table.component.scss"],
})
export class AdminTableComponent implements OnInit, OnChanges {
  @Input() zoneDict?: any = {};
  @Input() userDict?: any = {};
  @Input() tableColumns: any[] = [];
  @Input() tableData: any[] = [];
  @Input() tableActions: any[] = [];
  @Input() dataFrom!: string;
  @Input() skeletonLoader?: boolean = false;
  @Input() showFilters: boolean = false;
  @Input() showFilterRow?: boolean = false;
  @Output() emitTableAction = new EventEmitter<any>();
  @Output() emitTableSelection = new EventEmitter<any>();
  dateFilterApplied: {
    [key: string]: { isDirty: boolean; dateRange: Date[] };
  } = {};
  @ViewChild("adminTable") adminTable!: Table;

  selectedItems: any[] = [];
  skeletonData = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}];
  pagination = {
    limit: 5,
    options: [5, 10, 20, 30],
  };
  requestTypes: any = requestTypes;
  paginationFirst!: number;
  toggleStatus: boolean = false;
  popupEnabled: boolean = false;
  allSelected: boolean = false;
  currentRows: number = 10;
  selectAllEntries: boolean = false;

  sortField: string = "created_at";
  sortOrder: number = -1;

  experimentTypesMap: any = {
    assortment: "EXPERIMENT_TYPES.ASSORTMENT",
    planogram: "EXPERIMENT_TYPES.PLANOGRAM",
    space_assortment: "EXPERIMENT_TYPES.SPACE_PLANOGRAM",
    bulk_plano: "EXPERIMENT_TYPES.BULK_PLANO",
    "data exploration": "EXPERIMENT_TYPES.DATA_EXPLORATION",
    clustering: "EXPERIMENT_TYPES.CLUSTERING",
    data_exploration: "EXPERIMENT_TYPES.DATA_EXPLORATION",
  };

  columnFilters: { [key: string]: string } = {};
  filteredTableData: any[] | null = null;

  get displayData(): any[] {
    return this.filteredTableData ?? this.tableData;
  }

  constructor(
    private dialogRef: DynamicDialogRef,
    private dialogService: DialogService,
    private stateService: StateService,
    private httpService: HttpService,
    private msgService: MessageService,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {}

  get currentPageData(): any[] {
    const data = this.adminTable?.filteredValue ?? this.tableData;
    return data.slice(
      this.paginationFirst,
      this.paginationFirst + this.currentRows,
    );
  }

  get isPageAllSelected(): boolean {
    const page = this.currentPageData;
    return (
      page.length > 0 && page.every((item: any) => this.isRowSelected(item))
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.tableData) {
      this.paginationFirst = 0;
      this.tableData.forEach((e) => {
        e.created_at =
          typeof e.created_at === "string"
            ? new Date(e.created_at.split(".")[0])
            : e.created_at;
      });
      this.columnFilters = {};
    }
    if (changes["showFilters"] && !changes["showFilters"].currentValue) {
      this.clearAllFilters();
    }
    if (changes["showFilterRow"] && !changes["showFilterRow"].currentValue) {
      this.columnFilters = {};
      this.filteredTableData = null;
    }
    this.formFilterOptions(this.tableData);
  }

  applyColumnFilters(): void {
    // Filtering is handled via ngModel on the p-table's built-in filter or parent component
    // For admin-table, emit so parent can filter, or use PrimeNG's filterGlobal
    // Since tableData is passed as input, we filter a local copy
    const filters = this.columnFilters;
    const hasFilter = Object.values(filters).some((v) => v?.trim());
    if (!hasFilter) {
      this.filteredTableData = null;
    } else {
      this.filteredTableData = (this.tableData || []).filter((row) => {
        return Object.entries(filters).every(([key, val]) => {
          if (!val?.trim()) return true;
          const raw = row[key];
          const cellVal = Array.isArray(raw)
            ? raw
                .map((item: any) => item.name || item.title || item)
                .join(", ")
                .toLowerCase()
            : String(raw ?? "").toLowerCase();
          return cellVal.includes(val.toLowerCase());
        });
      });
    }
    this.paginationFirst = 0;
  }

  onHeaderCheckboxToggle(evt: any) {
    // evt.checked comes from header checkbox toggle; select only current page
    if (evt && evt.checked) {
      this.selectedItems = [...this.currentPageData];
      this.selectAllEntries = false;
    } else {
      this.selectedItems = [];
      this.selectAllEntries = false;
    }

    this.emitTableSelection.emit(this.selectedItems);
    this.emitMultipleRowData();
  }

  selectAllItems(): void {
    // Select all entries across pages (respecting filters)
    this.selectedItems = [
      ...(this.adminTable?.filteredValue ?? this.tableData),
    ];
    this.selectAllEntries = true;
    this.allSelected =
      this.selectedItems.length ===
      (this.adminTable?.filteredValue ?? this.tableData).length;
    this.emitTableSelection.emit(this.selectedItems);
    this.emitMultipleRowData();
  }

  onPageChange(event: any): void {
    this.paginationFirst = event.first;
    this.currentRows = event.rows;
  }

  checkRow(evt: any) {
    this.emitTableSelection.emit(this.selectedItems);
  }

  onTableAction(data: any, action: any) {
    this.emitTableAction.emit({ ...data, ...action });
  }

  isArray(val: any): boolean {
    return Array.isArray(val);
  }

  viewKeyAccounts(data: any, action: any) {
    this.emitTableAction.emit({ ...data, action: action });
  }

  openExperimentDetails(rowData: any) {
    this.dialogRef = this.dialogService.open(AdminModalComponent, {
      header: rowData.name,
      width: "20%",
      styleClass: "user-management-dialog experiment-details-dialog",
      contentStyle: { "max-height": "500px", overflow: "auto" },
      data: {
        type: "exp_details",
        experiment: rowData,
      },
    });

    this.dialogRef.onClose.subscribe((res: any) => {
      if (res) {
      }
    });
  }

  toggleUserStatus(event: any, data: any) {
    const status = event.checked;
    const index = this.tableData.findIndex((row) => row.id === data.id);
    this.tableData[index].is_active = status;

    const url = `${this.stateService.apiList.admin.getStatus(data.id)}`;
    const payload = {
      is_active: status,
    };

    this.httpService.patchMethod(url, payload).subscribe({
      next: (res: any) => {
        if (res) {
          this.showAlertMessage(
            "success",
            this.translate.instant("ADMIN.USER_STATUS"),
            this.translate.instant("ADMIN.STATUS_UPDATED"),
          );
        }
      },
      error: (error: any) => {
        this.showAlertMessage(
          "warn",
          this.translate.instant("ADMIN.USER_STATUS"),
          error?.error?.message,
        );
      },
    });
  }

  showAlertMessage(type: string, heading: string, message: string) {
    this.msgService.add({
      severity: type,
      summary: heading,
      detail: message,
    });
  }

  openDownloadPopup() {
    this.popupEnabled = !this.popupEnabled;
  }

  toggleAllSelection(event: CheckboxChangeEvent): void {
    // When toggling header checkbox, operate on current page only
    if (event.checked) {
      this.selectedItems = [...this.currentPageData];
    } else {
      this.selectedItems = []; // Deselect all
    }

    this.allSelected = this.isPageAllSelected;
    this.emitMultipleRowData();
  }

  toggleRowSelection(event: CheckboxChangeEvent, row: any): void {
    const nativeEvent = event.originalEvent;
    if (nativeEvent) {
      nativeEvent.stopPropagation();
    }

    const existingIndex = this.selectedItems.findIndex((r) => r.id === row.id);
    if (existingIndex !== -1) {
      this.selectedItems.splice(existingIndex, 1);
    } else {
      this.selectedItems.push(row);
    }
    // Keep the page-level "all selected" state in sync
    this.allSelected = this.isPageAllSelected;
    this.selectAllEntries = false;

    this.emitMultipleRowData();
  }

  isRowSelected(row: any): boolean {
    return this.selectedItems.some((r) => r.id === row.id); // assuming `id` is unique
  }

  // Helper method clear the table selection
  clearTableSelection() {
    this.allSelected = false;
    this.selectedItems = [];
    this.selectAllEntries = false;
    this.emitMultipleRowData();
  }

  emitMultipleRowData() {
    const option = {
      action: "move_multiple",
      callBack: "multipleActions",
    };
    this.emitTableAction.emit({ ...option, data: this.selectedItems });
  }

  // Helper method to filter all dropdown options for filtering the table data
  formFilterOptions(
    sourceData: any[] = this.tableData,
    skipFields: Set<string> = new Set(),
  ) {
    const rows = Array.isArray(sourceData) ? sourceData : [];

    this.tableColumns.forEach((e) => {
      if (e.filterType === "dropdown" && !skipFields.has(e.value)) {
        e.filterOptions = rows.reduce((acc, ele) => {
          const val = ele?.[e.value];
          if (val === undefined || val === null || val === "") {
            return acc;
          }
          if (!acc.some((item: any) => item.value === val)) {
            acc.push({
              label:
                e.value === "experiment_type"
                  ? this.experimentTypesMap[val]
                  : val,
              value: val,
            });
          }
          return acc;
        }, []);
      }
    });
  }

  onTableFilter() {
    setTimeout(() => {
      const visibleRows = Array.isArray(this.adminTable?.filteredValue)
        ? this.adminTable.filteredValue
        : this.tableData;
      const activeDropdownFilterFields = new Set(
        this.tableColumns
          .filter((column) => column?.filterType === "dropdown")
          .filter((column) => {
            const filterMeta = this.adminTable?.filters?.[column.value] as any;
            const filterValue = Array.isArray(filterMeta)
              ? filterMeta[0]?.value
              : filterMeta?.value;

            if (Array.isArray(filterValue)) {
              return filterValue.length > 0;
            }

            return (
              filterValue !== undefined &&
              filterValue !== null &&
              filterValue !== ""
            );
          })
          .map((column) => column.value),
      );

      this.formFilterOptions(visibleRows, activeDropdownFilterFields);
    });
  }

  // Helper method to close and validate selected date range for date filter
  handleClickOutside(event: any, field: string) {
    if (!this.dateFilterApplied[field]?.isDirty) {
      event.value = [];
    } else {
      event.value = this.dateFilterApplied[field].dateRange;
    }
  }

  // Helper method to disable date filter's "apply" btn if no date selected
  isDatesSelected(dateRange: Date[]) {
    const validDateRange = dateRange?.filter((e: Date) => e != null);
    return !dateRange || (validDateRange && validDateRange.length < 1)
      ? true
      : false;
  }

  // Helper method to filter all data based on selected date range
  applyDateFilter(value: Date[], filter: Function, field: string) {
    if (!value || value.length === 0) {
      filter(null);
      this.updateDateFilter(field, false, []);
      return;
    }

    let start: Date;
    let end: Date;

    // If only one month selected
    if (value.length === 1 || !value[1]) {
      const date = value[0];

      start = new Date(date.getFullYear(), date.getMonth(), 1);
      end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    }
    // Range selected
    else {
      const startDate = value[0];
      const endDate = value[1];

      start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      end = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
    }

    filter([start, end]);
    this.updateDateFilter(field, true, [start, end]);
  }

  resetCalendar(filter: any, field: string) {
    this.updateDateFilter(field, false, []);
    filter(null);
  }

  // Helper method to track if date filter is applied
  updateDateFilter(field: string, isDirty: boolean, dateRange: Date[]) {
    this.dateFilterApplied[field] = {
      isDirty,
      dateRange,
    };
  }

  // Helper method to filter all data based on selected column
  filterData(event: any, filter: any, filterType: string) {
    let val: any;
    if (filterType === "input_text") {
      val = event?.target?.value;
    } else if (filterType === "dropdown") {
      val = event?.map((e: any) => e.value);
    }
    filter(val);
  }

  // Helper method to clear all filter on filter option close
  clearAllFilters() {
    this.adminTable?.clear();
    this.columnFilters = {};
    this.dateFilterApplied = {};
    this.formFilterOptions(this.tableData);
  }

  isSortedColumn(column: any): boolean {
    return this.sortField === column.value;
  }

  isAscending(column: any): boolean {
    return this.isSortedColumn(column) && this.sortOrder === 1;
  }

  isDescending(column: any): boolean {
    return this.isSortedColumn(column) && this.sortOrder === -1;
  }

  handleHeaderClick(event: Event, column: any) {
    if (!column?.sortable) {
      event.stopPropagation();
      return;
    }
    if (this.sortField === column.value) {
      this.sortOrder = this.sortOrder === 1 ? -1 : 1;
    } else {
      this.sortField = column.value;
      this.sortOrder = 1;
    }
  }

  // Helper method to format date in a readable format
  formatReadableDate(dateStr: string | Date): string {
    if (!dateStr) return "";
    const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  }
}
